import React, { useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import PropTypes from "prop-types";
import {
  Card,
  Chip,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
} from "@mui/material";

import Constants from "Helpers/Constants";
import init from "Helpers/WindowToken";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDTypography from "components/MDTypography";
import PurchaseOfferManager from "./PurchaseOfferManager";

const cardSx = {
  background: "linear-gradient(145deg, #202940 0%, #1B2438 100%)",
  border: "1px solid rgba(255, 255, 255, 0.08)",
};

const detailSx = {
  height: "100%",
  minHeight: 88,
  p: 1.75,
  borderRadius: "12px",
  backgroundColor: "rgba(255, 255, 255, 0.045)",
  border: "1px solid rgba(255, 255, 255, 0.08)",
};

const emptyGrantForm = {
  plan_id: "",
  subscription_start_at: "",
  subscription_end_at: "",
  reason: "",
  internal_note: "",
};

const emptyRevokeForm = {
  reason: "",
  internal_note: "",
};

const formatDate = (value) => {
  if (!value) {
    return "-";
  }

  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return "-";
  }

  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const createIdempotencyKey = (action, userId) => {
  const randomValue =
    typeof window !== "undefined" && window.crypto?.randomUUID
      ? window.crypto.randomUUID()
      : `${Date.now()}-${Math.random().toString(36).slice(2)}`;

  return `dashboard-${action}-${userId}-${randomValue}`;
};

function UserSubscriptionAccess({ userId, onNotify }) {
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [plans, setPlans] = useState([]);
  const [subscription, setSubscription] = useState(null);
  const [grantDialogOpen, setGrantDialogOpen] = useState(false);
  const [revokeDialogOpen, setRevokeDialogOpen] = useState(false);
  const [grantForm, setGrantForm] = useState(emptyGrantForm);
  const [revokeForm, setRevokeForm] = useState(emptyRevokeForm);

  const loadSubscriptionAccess = useCallback(async () => {
    if (!userId || init() !== "success") {
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const [subscriptionResponse, plansResponse] = await Promise.all([
        axios.get(`${Constants.getUrls.getUserSubscription}${userId}/subscription`),
        axios.get(Constants.getUrls.getSubscriptionPlans),
      ]);
      const nextPlans = Array.isArray(plansResponse?.data?.data)
        ? plansResponse.data.data
        : [];

      setSubscription(subscriptionResponse?.data?.data?.subscription || null);
      setPlans(nextPlans);
      setGrantForm((current) => ({
        ...current,
        plan_id:
          current.plan_id && nextPlans.some((plan) => plan?._id === current.plan_id)
            ? current.plan_id
            : nextPlans.find((plan) => plan?.is_active)?._id || nextPlans[0]?._id || "",
      }));
    } catch (error) {
      onNotify(error?.response?.data?.message || "Unable to load subscription access.", "error");
    } finally {
      setLoading(false);
    }
  }, [onNotify, userId]);

  useEffect(() => {
    loadSubscriptionAccess();
  }, [loadSubscriptionAccess]);

  const hasSubscription = Boolean(
    subscription?.subscription_id && subscription?.status && subscription.status !== "NONE"
  );
  const selectedPlan = useMemo(
    () => plans.find((plan) => plan?._id === grantForm.plan_id) || null,
    [grantForm.plan_id, plans]
  );

  const openGrantDialog = () => {
    setGrantForm((current) => ({
      ...emptyGrantForm,
      plan_id:
        current.plan_id || plans.find((plan) => plan?.is_active)?._id || plans[0]?._id || "",
    }));
    setGrantDialogOpen(true);
  };

  const handleGrant = async () => {
    const reason = grantForm.reason.trim();
    const internalNote = grantForm.internal_note.trim();

    if (!grantForm.plan_id || !reason || !internalNote) {
      onNotify("Select a plan and enter both the reason and internal note.", "error");
      return;
    }

    if (
      grantForm.subscription_start_at &&
      grantForm.subscription_end_at &&
      new Date(grantForm.subscription_end_at) <= new Date(grantForm.subscription_start_at)
    ) {
      onNotify("Subscription end date must be after the start date.", "error");
      return;
    }

    try {
      setSaving(true);
      const payload = {
        plan_id: grantForm.plan_id,
        reason,
        internal_note: internalNote,
        idempotency_key: createIdempotencyKey("grant", userId),
      };

      if (grantForm.subscription_start_at) {
        payload.subscription_start_at = grantForm.subscription_start_at;
      }
      if (grantForm.subscription_end_at) {
        payload.subscription_end_at = grantForm.subscription_end_at;
      }

      const response = await axios.post(
        `${Constants.postUrls.grantUserSubscription}${userId}/subscription/grant`,
        payload
      );

      setGrantDialogOpen(false);
      setGrantForm(emptyGrantForm);
      onNotify(response?.data?.message || "Subscription allotted successfully.", "success");
      await loadSubscriptionAccess();
    } catch (error) {
      onNotify(error?.response?.data?.message || "Unable to allot subscription.", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleRevoke = async () => {
    const reason = revokeForm.reason.trim();
    const internalNote = revokeForm.internal_note.trim();

    if (!reason || !internalNote) {
      onNotify("Enter both the revocation reason and internal note.", "error");
      return;
    }

    try {
      setSaving(true);
      const response = await axios.post(
        `${Constants.postUrls.revokeUserSubscription}${userId}/subscription/revoke`,
        {
          reason,
          internal_note: internalNote,
          idempotency_key: createIdempotencyKey("revoke", userId),
        }
      );

      setRevokeDialogOpen(false);
      setRevokeForm(emptyRevokeForm);
      onNotify(response?.data?.message || "Subscription access revoked.", "success");
      await loadSubscriptionAccess();
    } catch (error) {
      onNotify(error?.response?.data?.message || "Unable to revoke subscription.", "error");
    } finally {
      setSaving(false);
    }
  };

  return (
    <>
      <Card sx={{ ...cardSx, mt: 3, mb: 3 }}>
        <MDBox p={2.5}>
          <MDBox
            display="flex"
            justifyContent="space-between"
            alignItems={{ xs: "flex-start", md: "center" }}
            flexDirection={{ xs: "column", md: "row" }}
            gap={1.5}
          >
            <MDBox>
              <MDTypography variant="h6" color="white">
                Subscription Access
              </MDTypography>
              <MDTypography variant="button" color="text">
                Allot a plan to this user or revoke their current subscription access.
              </MDTypography>
            </MDBox>

            <MDBox display="flex" gap={1} flexWrap="wrap">
              <MDButton
                color="success"
                variant="contained"
                disabled={loading || hasSubscription || plans.length === 0}
                onClick={openGrantDialog}
              >
                Allot Subscription
              </MDButton>
              <MDButton
                color="error"
                variant="outlined"
                disabled={loading || !hasSubscription}
                onClick={() => setRevokeDialogOpen(true)}
                sx={{ color: "#FF8A80 !important", borderColor: "#EF5350 !important" }}
              >
                Revoke Access
              </MDButton>
            </MDBox>
          </MDBox>

          <MDBox mt={2.5}>
            {loading ? (
              <MDTypography variant="button" color="text">
                Loading subscription access...
              </MDTypography>
            ) : hasSubscription ? (
              <Grid container spacing={1.5}>
                <Grid item xs={12} md={4}>
                  <MDBox sx={detailSx}>
                    <MDBox display="flex" alignItems="center" gap={1} flexWrap="wrap">
                      <MDTypography variant="button" color="white" fontWeight="bold">
                        {subscription?.current_plan?.plan_name || "Subscription"}
                      </MDTypography>
                      <Chip label={subscription?.status || "ACTIVE"} color="success" size="small" />
                    </MDBox>
                    <MDTypography variant="caption" color="text" display="block" mt={0.75}>
                      {subscription?.current_plan?.billing_period || "-"} • Version {subscription?.current_plan?.plan_version || 1}
                    </MDTypography>
                  </MDBox>
                </Grid>
                <Grid item xs={12} sm={6} md={2}>
                  <MDBox sx={detailSx}>
                    <MDTypography variant="caption" color="text">
                      Validity
                    </MDTypography>
                    <MDTypography variant="button" color="white" display="block" mt={0.5}>
                      {formatDate(subscription?.start_date)}
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      to {formatDate(subscription?.end_date)}
                    </MDTypography>
                  </MDBox>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MDBox sx={detailSx}>
                    <MDTypography variant="caption" color="text">
                      Included Tests
                    </MDTypography>
                    <MDTypography variant="h6" color="white" mt={0.5}>
                      {subscription?.included_tests?.remaining || 0} / {subscription?.included_tests?.total || 0}
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      remaining
                    </MDTypography>
                  </MDBox>
                </Grid>
                <Grid item xs={12} sm={6} md={3}>
                  <MDBox sx={detailSx}>
                    <MDTypography variant="caption" color="text">
                      Subscription AI Credits
                    </MDTypography>
                    <MDTypography variant="h6" color="white" mt={0.5}>
                      {subscription?.subscription_ai_credits?.remaining || 0} / {subscription?.subscription_ai_credits?.total || 0}
                    </MDTypography>
                    <MDTypography variant="caption" color="text">
                      remaining
                    </MDTypography>
                  </MDBox>
                </Grid>
              </Grid>
            ) : (
              <MDBox sx={{ ...detailSx, minHeight: 0 }}>
                <MDTypography variant="button" color="white" fontWeight="medium">
                  No active subscription
                </MDTypography>
                <MDTypography variant="caption" color="text" display="block">
                  Choose Allot Subscription to give this user access to any configured plan.
                </MDTypography>
              </MDBox>
            )}
          </MDBox>
        </MDBox>
      </Card>

      <PurchaseOfferManager userId={userId} onNotify={onNotify} />

      <Dialog
        open={grantDialogOpen}
        onClose={saving ? undefined : () => setGrantDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { backgroundColor: "#202940", backgroundImage: "none" } }}
      >
        <DialogTitle sx={{ color: "#fff" }}>Allot Subscription</DialogTitle>
        <DialogContent>
          <MDTypography variant="button" color="text" display="block" mb={2}>
            Select a plan and record why access is being granted. If dates are left blank, access starts now and uses the plan&apos;s configured duration.
          </MDTypography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth>
                <InputLabel>Subscription Plan</InputLabel>
                <Select
                  value={grantForm.plan_id}
                  label="Subscription Plan"
                  onChange={(event) =>
                    setGrantForm((current) => ({ ...current, plan_id: event.target.value }))
                  }
                >
                  {plans.map((plan) => (
                    <MenuItem key={plan?._id} value={plan?._id}>
                      {plan?.name} • {plan?.duration_days || 0} days
                      {plan?.is_active ? "" : " • Inactive"}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            {selectedPlan ? (
              <Grid item xs={12}>
                <MDBox sx={{ ...detailSx, minHeight: 0 }}>
                  <MDTypography variant="caption" color="text">
                    {selectedPlan?.code} • {selectedPlan?.billing_period || "custom"} • Tests: {selectedPlan?.included_test_count || 0} • AI credits: {selectedPlan?.included_subscription_ai_credits || 0}
                  </MDTypography>
                </MDBox>
              </Grid>
            ) : null}
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="Start Date (optional)"
                InputLabelProps={{ shrink: true }}
                value={grantForm.subscription_start_at}
                onChange={(event) =>
                  setGrantForm((current) => ({
                    ...current,
                    subscription_start_at: event.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                type="date"
                label="End Date (optional)"
                InputLabelProps={{ shrink: true }}
                value={grantForm.subscription_end_at}
                onChange={(event) =>
                  setGrantForm((current) => ({
                    ...current,
                    subscription_end_at: event.target.value,
                  }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Reason"
                placeholder="Example: Scholarship benefit"
                value={grantForm.reason}
                onChange={(event) =>
                  setGrantForm((current) => ({ ...current, reason: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Internal Note"
                placeholder="Visible only to administrators"
                value={grantForm.internal_note}
                onChange={(event) =>
                  setGrantForm((current) => ({
                    ...current,
                    internal_note: event.target.value,
                  }))
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <MDButton color="secondary" disabled={saving} onClick={() => setGrantDialogOpen(false)}>
            Cancel
          </MDButton>
          <MDButton color="success" disabled={saving} onClick={handleGrant}>
            {saving ? "Allotting..." : "Confirm Allotment"}
          </MDButton>
        </DialogActions>
      </Dialog>

      <Dialog
        open={revokeDialogOpen}
        onClose={saving ? undefined : () => setRevokeDialogOpen(false)}
        maxWidth="sm"
        fullWidth
        PaperProps={{ sx: { backgroundColor: "#202940", backgroundImage: "none" } }}
      >
        <DialogTitle sx={{ color: "#fff" }}>Revoke Subscription Access</DialogTitle>
        <DialogContent>
          <MDTypography variant="button" color="text" display="block" mb={2}>
            This immediately ends {subscription?.current_plan?.plan_name || "the current plan"} and sets the remaining included tests and subscription AI credits to zero.
          </MDTypography>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Revocation Reason"
                placeholder="Example: Access granted by mistake"
                value={revokeForm.reason}
                onChange={(event) =>
                  setRevokeForm((current) => ({ ...current, reason: event.target.value }))
                }
              />
            </Grid>
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                minRows={2}
                label="Internal Note"
                placeholder="Visible only to administrators"
                value={revokeForm.internal_note}
                onChange={(event) =>
                  setRevokeForm((current) => ({
                    ...current,
                    internal_note: event.target.value,
                  }))
                }
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{ px: 3, pb: 2.5 }}>
          <MDButton color="secondary" disabled={saving} onClick={() => setRevokeDialogOpen(false)}>
            Cancel
          </MDButton>
          <MDButton color="error" disabled={saving} onClick={handleRevoke}>
            {saving ? "Revoking..." : "Revoke Access"}
          </MDButton>
        </DialogActions>
      </Dialog>
    </>
  );
}

UserSubscriptionAccess.propTypes = {
  userId: PropTypes.string.isRequired,
  onNotify: PropTypes.func,
};

UserSubscriptionAccess.defaultProps = {
  onNotify: () => {},
};

export default UserSubscriptionAccess;
